// Word list loading, bundle grouping and the persisted "which lists to study" choice.
import { normalize } from './spelling';

const STORAGE_KEY = 'wordLists';

export const parseCSVLine = (line) => {
  const fields = []; let value = ''; let quoted = false;
  for (let i = 0; i < line.length; i += 1) {
    if (line[i] === '"' && quoted && line[i + 1] === '"') { value += '"'; i += 1; }
    else if (line[i] === '"') quoted = !quoted;
    else if (line[i] === ',' && !quoted) { fields.push(value.trim()); value = ''; }
    else value += line[i];
  }
  fields.push(value.trim()); return fields;
};

const splitList = (value = '') => value.split(';').map(s => s.trim()).filter(Boolean);

// public/words.csv → [{ id, word, accepted, difficulty, bundles }]. Columns are read by
// header so optional fields (e.g. definition) simply come through when present.
export const parseWords = (text) => {
  const lines = text.split(/\r?\n/).filter(Boolean);
  if (!lines.length) return [];
  const header = parseCSVLine(lines[0]);
  return lines.slice(1).map(line => {
    const row = Object.fromEntries(parseCSVLine(line).map((field, i) => [header[i], field]));
    const accepted = [...new Set([row.word, ...splitList(row.alternate_spellings), ...splitList(row.allowable_spellings)]
      .map(normalize).filter(Boolean))];
    return { ...row, accepted, difficulty: Number(row.difficulty) || 0, bundles: splitList(row.bundles) };
  }).filter(row => row.word);
};

// public/definitions.csv (from tools/build_definitions.py) → { id: { partOfSpeech, definition } }
export const parseDefinitions = (text) => {
  const lines = text.split(/\r?\n/).filter(Boolean);
  if (!lines.length) return {};
  const header = parseCSVLine(lines[0]);
  const map = {};
  lines.slice(1).forEach(line => {
    const row = Object.fromEntries(parseCSVLine(line).map((field, i) => [header[i], field]));
    if (row.id && row.definition) map[row.id] = { partOfSpeech: row.part_of_speech || '', definition: row.definition };
  });
  return map;
};

// Attach definitions to words; words without one are left as they are, so the
// Definition button simply does not appear for them.
export const mergeDefinitions = (words, defs) => words.map(w => defs[w.id] ? { ...w, ...defs[w.id] } : w);

// "2027 Words of the Champions: Two Bee, C" → group "Words of the Champions · Two Bee", label "C"
// "2027 School Spelling Bee Study List: 1st, 2nd and 3rd Grade Words" → group "School Study List", label "1st–3rd Grade"
const describeBundle = (name) => {
  const [prefix, rest = ''] = name.split(/:\s*/, 2);
  const series = prefix.replace(/^\d{4}\s*/, '');
  if (/Words of the Champions/i.test(series)) {
    const [level, letters] = rest.split(/,\s*/, 2);
    return { group: `${series} · ${level}`, label: letters || level };
  }
  if (/School/i.test(series)) {
    const grades = rest.match(/\d+(?:st|nd|rd|th)/g) || [];
    const label = grades.length > 1 ? `${grades[0]}–${grades[grades.length - 1]} Grade` : rest.replace(/ Words$/, '');
    return { group: 'School Study List', label };
  }
  return { group: series, label: rest || name };
};

// Groups in order of first appearance: [{ title, bundles: [{ name, label, count }] }]
export const groupBundles = (words) => {
  const groups = new Map();
  words.forEach(word => word.bundles.forEach(name => {
    const { group, label } = describeBundle(name);
    if (!groups.has(group)) groups.set(group, { title: group, bundles: new Map() });
    const bundles = groups.get(group).bundles;
    if (!bundles.has(name)) bundles.set(name, { name, label, count: 0 });
    bundles.get(name).count += 1;
  }));
  return [...groups.values()].map(g => ({ ...g, bundles: [...g.bundles.values()] }));
};

export const allBundleNames = (words) => [...new Set(words.flatMap(w => w.bundles))];

// What a first-time visitor studies: the two upper School Study Lists (~300 words).
// Falls back to everything if those bundles are ever renamed out of the CSV.
const DEFAULT_BUNDLES = [/School.*4th.*5th.*6th/i, /School.*7th.*8th/i];
export const defaultSelection = (words) => {
  const all = allBundleNames(words);
  const chosen = all.filter(name => DEFAULT_BUNDLES.some(pattern => pattern.test(name)));
  return chosen.length ? chosen : all;
};

// Stored selection, or the default lists when nothing usable is stored.
export const loadSelection = (words) => {
  const all = allBundleNames(words);
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY));
    if (Array.isArray(stored)) {
      const valid = stored.filter(name => all.includes(name));
      if (valid.length) return valid;
    }
  } catch (_) {}
  return defaultSelection(words);
};

export const saveSelection = (names) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(names)); } catch (_) {}
};

export const filterWords = (words, selected) => {
  const chosen = new Set(selected);
  return words.filter(w => w.bundles.some(b => chosen.has(b)));
};
